import { useCallback, type FC, useEffect, useState } from "react";
import TradeHeader from "../Trade/views/TradHeader";
import TradeBodyAmountList from "./views/TradeBodyAmountList";
import TradeBodySwap from "./views/TradeBodySwap";
import TradeHistory from "./views/TradeHistory";
import { ActionType, ORDER_DIRECTION } from "~/pages/constants";
import useSWR from "swr";
import { useSnackbar } from "~/Provider/Snackbar";
import { fetchTokenBalances, fetchTokenTradePair } from "~/api/endpoints";
import TradeResultPopup from "./TradeResultPopup/TradeResultPopup";
import useTradeTokenInfo from "./hooks/useTradeTokenInfo";
import {
    PairedToken,
    TokenBase,
    TokenWithBalance,
    TradeOrder,
    TradeToken,
} from "~/types";
import { isSameToken } from "./utils";
import { useUXUYWallet } from "~/Provider/UXUYWallet";
import useSlippageSetting from "./hooks/useSlippageSetting";
import { Page } from "~/components/Page/Page";

const TradePage: FC = () => {
    const { getAddress, getAddressType } = useUXUYWallet();
    const slippageValue = useSlippageSetting();
    const snackbar = useSnackbar();

    const {
        tradeActiveType,
        setTradeActiveType,
        tradeToken,
        setTradeTokenPure,
        pairedToken,
        setPairedToken,
    } = useTradeTokenInfo();

    const tradeTokenChainId = String(tradeToken?.token_base?.chain_id);
    const address = getAddress(tradeTokenChainId);
    const addressType = getAddressType(tradeTokenChainId);
    const [tradeOrderId, setTradeOrderId] = useState<string | undefined>();

    const queryTokenTradePair = useCallback(
        async ([chainId, contract, address, address_type]: any) => {
            if (!chainId) return;

            try {
                const data = await fetchTokenTradePair({
                    chain_id: chainId,
                    contract,
                    address,
                    address_type,
                });
                return data.data;
            } catch (err: any) {
                if ("code" in err) {
                    snackbar.error(err.message);
                    throw err;
                }

                snackbar.error(
                    `Can not load token info! Check your network connection!`
                );
                throw err;
            }
        },
        []
    );

    const {
        data: tokenPairResult,
        isLoading: loading,
        mutate,
    } = useSWR(
        [
            tradeToken?.token_base?.chain_id,
            tradeToken?.token_base?.contract,
            address,
            addressType,
            "queryTokenTradePair",
        ],
        queryTokenTradePair as any
    );

    const [isFavorite, setIsFavorite] = useState(false);

    const updateFavoriteCache = (newFavorite: boolean) => {
        mutate(
            {
                ...tokenPairResult,
                to: {
                    ...tokenPairResult.to,
                    token_base: {
                        ...tokenPairResult.to.token_base,
                        favorite: newFavorite,
                    },
                },
            },
            { revalidate: true }
        );
    };

    useEffect(() => {
        if (loading) return;
        if (!tokenPairResult) return;

        if (isSameToken(tradeToken, tokenPairResult?.to)) {
            setTradeTokenPure(tokenPairResult.to);
            setIsFavorite(tokenPairResult.to.token_base.favorite ?? false);

            if (!pairedToken) {
                setPairedToken(tokenPairResult.to, tokenPairResult.from);
            }
        }
    }, [
        loading,
        tokenPairResult,
        tradeToken?.token_base?.chain_id,
        tradeToken?.token_base?.contract?.toLowerCase(),
        pairedToken?.token_base?.chain_id,
        pairedToken?.token_base?.contract.toLowerCase(),
    ]);

    const queryTokenBalances = useCallback(async (args: any) => {
        const [
            address,
            address_type,
            tradeTokenChainId,
            tradeTokenContract,
            pairedTokenChainId,
            pairedTokenContract,
        ] = args;
        if (!address) return;

        if (
            !tradeTokenChainId ||
            !tradeTokenContract ||
            !pairedTokenChainId ||
            !pairedTokenContract
        ) {
            return;
        }

        const tokens = [
            {
                chain_id: String(tradeTokenChainId),
                contract: String(tradeTokenContract),
            },
            {
                chain_id: String(pairedTokenChainId),
                contract: String(pairedTokenContract),
            },
        ];

        return fetchTokenBalances(address, address_type, tokens).then(
            (res) => res.data && res.data.list
        );
    }, []);

    const { data: balancesData } = useSWR(
        [
            address,
            addressType,
            tradeToken?.token_base?.chain_id,
            tradeToken?.token_base?.contract,
            pairedToken?.token_base?.chain_id,
            pairedToken?.token_base?.contract,
            tradeOrderId,
            "onceUpdateBalance",
        ],
        queryTokenBalances
    );

    const updateBalacce = useCallback(
        (
            balancesData: Array<TokenWithBalance> | null | undefined,
            tradeToken: TradeToken,
            pairedToken: PairedToken
        ) => {
            if (!balancesData) return;

            const newTradeToken = balancesData[0];
            const newPairedToken = balancesData[1];

            if (
                !isSameToken(tradeToken, newTradeToken) ||
                !isSameToken(pairedToken, newPairedToken)
            ) {
                return;
            }

            setTradeTokenPure(newTradeToken);
            setPairedToken(newTradeToken, newPairedToken);
        },
        []
    );

    useEffect(() => {
        updateBalacce(balancesData, tradeToken, pairedToken);
    }, [balancesData, tradeToken, pairedToken]);

    const { data: intervalBalancesData } = useSWR(
        [
            address,
            addressType,
            tradeToken?.token_base?.chain_id,
            tradeToken?.token_base?.contract,
            pairedToken?.token_base?.chain_id,
            pairedToken?.token_base?.contract,
            tradeOrderId,
            "useIntervalUpdateBalance",
        ],
        queryTokenBalances,
        { refreshInterval: 8000 }
    );

    useEffect(() => {
        updateBalacce(intervalBalancesData, tradeToken, pairedToken);
    }, [intervalBalancesData, tradeToken, pairedToken]);

    const handleReverseTrade = (order: TradeOrder) => {
        if (order.direction === ORDER_DIRECTION.buy) {
            const newTradeToken = {
                token_base: order.to_token as unknown as TokenBase,
            };
            const newPairedToken = {
                token_base: order.from_token as unknown as TokenBase,
            };
            setTradeTokenPure(newTradeToken);
            setPairedToken(newTradeToken, newPairedToken);
            setTradeActiveType(newTradeToken, ActionType.Sell);
        } else {
            const newTradeToken = {
                token_base: order.from_token as unknown as TokenBase,
            };
            const newPairedToken = {
                token_base: order.to_token as unknown as TokenBase,
            };
            setTradeTokenPure(newTradeToken);
            setPairedToken(newTradeToken, newPairedToken);
            setTradeActiveType(newTradeToken, ActionType.Buy);
        }
    };

    return (
        <Page>
            <TradeResultPopup tradeOrderId={tradeOrderId} setTradeOrderId={setTradeOrderId} />
            <TradeHeader
                token={tradeToken}
                loading={loading}
                isFavorite={isFavorite}
                setIsFavorite={setIsFavorite}
                updateFavoriteCache={updateFavoriteCache}
            />
            <div className="flex px-4 mb-[20px]">
                <div className="flex-none w-2/5 relative">
                    <div className="absolute top-0 left-0 right-0 bottom-0 pr-3">
                        <TradeBodyAmountList
                            loading={loading}
                            token={tradeToken}
                        />
                    </div>
                </div>
                <div className="flex-none w-3/5">
                    <TradeBodySwap
                        loading={loading}
                        tradeActionType={tradeActiveType}
                        setTradeActiveType={setTradeActiveType}
                        tradeToken={tradeToken}
                        pairedToken={pairedToken}
                        setPairedToken={setPairedToken}
                        slippageValue={slippageValue}
                        setTradeOrderId={setTradeOrderId}
                    />
                </div>
            </div>
            <TradeHistory
                token={tradeToken}
                tradeOrderId={tradeOrderId}
                onReverseTrade={handleReverseTrade}
            />
        </Page>
    );
};

export default TradePage;
